import { forwardRef } from "react";

interface EducationalExplainerCardProps {
  message: string;
  pattern: string;
  secondaryPattern?: string;
  signals: string[];
  interpretation: string;
  manipulationScore: number;
  emotionalHarmScore: number;
}

const EducationalExplainerCard = forwardRef<HTMLDivElement, EducationalExplainerCardProps>(
  ({ message, pattern, secondaryPattern, signals, interpretation, manipulationScore, emotionalHarmScore }, ref) => {
    const scoreColor = (v: number) =>
      v >= 7 ? "#ef4444" : v >= 4 ? "#f59e0b" : "#64748b";

    return (
      <div
        ref={ref}
        style={{
          position: "absolute",
          left: "-9999px",
          top: "-9999px",
          width: 1080,
          height: 1350,
          background: "#0c0c0c",
          fontFamily: "'Inter', system-ui, sans-serif",
          boxSizing: "border-box",
          overflow: "hidden",
          padding: "72px 80px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Top branding bar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 52,
          }}
        >
          <div
            style={{
              fontSize: 15,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
            color: "rgba(255,255,255,0.50)",
              fontFamily: "'JetBrains Mono', monospace",
            }}
          >
            GreyRockAI
          </div>
          <div
            style={{
              fontSize: 13,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.2)",
              fontFamily: "'JetBrains Mono', monospace",
            }}
          >
            Forensic Analysis
          </div>
        </div>

        {/* Analyzed phrase */}
        <div style={{ marginBottom: 40 }}>
          <div
            style={{
              fontSize: 11,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.3)",
              marginBottom: 14,
              fontFamily: "'JetBrains Mono', monospace",
            }}
          >
            Analyzed Phrase
          </div>
          <div
            style={{
              fontSize: 26,
              lineHeight: "40px",
              color: "rgba(255,255,255,0.85)",
              fontWeight: 300,
              fontStyle: "italic",
              borderLeft: "3px solid rgba(255,255,255,0.15)",
              paddingLeft: 24,
              wordBreak: "break-word",
            }}
          >
            "{message.slice(0, 180)}"
          </div>
        </div>

        {/* ── Main payload: Pattern + Interpretation ── */}
        <div style={{ marginBottom: 32 }}>
          <div
            style={{
              fontSize: 11,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.3)",
              marginBottom: 14,
              fontFamily: "'JetBrains Mono', monospace",
            }}
          >
            Detected Pattern
          </div>
          <div
            style={{
              fontSize: 32,
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.95)",
              marginBottom: 18,
            }}
          >
            {pattern}
          </div>
          {secondaryPattern && (
            <div
              style={{
                fontSize: 18,
                fontWeight: 500,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.4)",
                marginBottom: 12,
              }}
            >
              Also detected: {secondaryPattern}
            </div>
          )}
          {/* Interpretation — directly under pattern */}
          <div
            style={{
              fontSize: 22,
              lineHeight: "34px",
              color: "rgba(255,255,255,0.7)",
              fontWeight: 400,
              maxWidth: 820,
            }}
          >
            {interpretation}
          </div>
        </div>

        {/* Signal chips */}
        {signals.length > 0 && (
          <div style={{ marginBottom: 36 }}>
            <div
              style={{
                fontSize: 11,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.3)",
                marginBottom: 14,
                fontFamily: "'JetBrains Mono', monospace",
              }}
            >
              Signals
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {signals.slice(0, 4).map((s, i) => (
                <span
                  key={i}
                  style={{
                    display: "inline-block",
                    padding: "10px 22px",
                    borderRadius: 24,
                    fontSize: 16,
                    fontFamily: "'JetBrains Mono', monospace",
                    color: "#00f2ff",
                    background: "rgba(0,242,255,0.06)",
                    border: "1px solid rgba(0,242,255,0.2)",
                    letterSpacing: "0.04em",
                  }}
                >
                  {s}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Score pair */}
        <div
          style={{
            display: "flex",
            gap: 40,
            marginBottom: 48,
            paddingTop: 36,
            borderTop: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          {[
            { label: "Manipulation", value: manipulationScore },
            { label: "Emotional Harm", value: emotionalHarmScore },
          ].map(({ label, value }) => (
            <div key={label} style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
              <span
                style={{
                  fontSize: 40,
                  fontWeight: 700,
                  color: scoreColor(value),
                  fontFamily: "'JetBrains Mono', monospace",
                  lineHeight: 1,
                }}
              >
                {value.toFixed(1)}
              </span>
              <span
                style={{
                  fontSize: 13,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: "rgba(255,255,255,0.35)",
                  fontFamily: "'JetBrains Mono', monospace",
                }}
              >
                {label}
              </span>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div
          style={{
            fontSize: 15,
            letterSpacing: "0.15em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.60)",
            fontWeight: 500,
            textAlign: "center",
            fontFamily: "'JetBrains Mono', monospace",
          }}
        >
          ANALYZE YOURS → GREYROCKAI.APP®
        </div>
      </div>
    );
  }
);

EducationalExplainerCard.displayName = "EducationalExplainerCard";
export default EducationalExplainerCard;
